import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import io from 'socket.io-client';

/**
 * Resolves the true traffic source for a page view.
 * Priority: UTM params > Click IDs (fbclid, gclid, etc.) > document.referrer > Direct
 */
const resolveTrafficSource = () => {
    const params = new URLSearchParams(window.location.search);

    // 1. Explicit UTM source (highest priority — set by marketer)
    const utmSource   = params.get('utm_source')   || '';
    const utmMedium   = params.get('utm_medium')   || '';
    const utmCampaign = params.get('utm_campaign') || '';

    if (utmSource) {
        return { trafficSource: utmSource, utmSource, utmMedium, utmCampaign };
    }

    // 2. Platform-specific click IDs (referrer is stripped by these platforms)
    if (params.get('fbclid')) {
        return { trafficSource: 'Facebook', utmSource: 'facebook', utmMedium: 'social', utmCampaign };
    }
    if (params.get('gclid')) {
        return { trafficSource: 'Google Ads', utmSource: 'google', utmMedium: 'cpc', utmCampaign };
    }
    if (params.get('ttclid')) {
        return { trafficSource: 'TikTok', utmSource: 'tiktok', utmMedium: 'social', utmCampaign };
    }
    if (params.get('igshid') || params.get('ig_source')) {
        return { trafficSource: 'Instagram', utmSource: 'instagram', utmMedium: 'social', utmCampaign };
    }
    if (params.get('li_fat_id')) {
        return { trafficSource: 'LinkedIn', utmSource: 'linkedin', utmMedium: 'social', utmCampaign };
    }
    if (params.get('twclid')) {
        return { trafficSource: 'Twitter / X', utmSource: 'twitter', utmMedium: 'social', utmCampaign };
    }
    if (params.get('msclkid')) {
        return { trafficSource: 'Bing Ads', utmSource: 'bing', utmMedium: 'cpc', utmCampaign };
    }

    // 3. Referrer header (when platform allows it)
    const ref = document.referrer;
    if (ref) {
        try {
            const refUrl = new URL(ref);
            let domain   = refUrl.hostname.replace(/^www\./, '');

            if (domain.includes('google.'))    domain = 'Google';
            else if (domain.includes('bing.')) domain = 'Bing';
            else if (domain.includes('facebook.') || domain.includes('fb.com')) domain = 'Facebook';
            else if (domain.includes('instagram.')) domain = 'Instagram';
            else if (domain.includes('twitter.') || domain === 'x.com' || domain.includes('t.co')) domain = 'Twitter / X';
            else if (domain.includes('linkedin.')) domain = 'LinkedIn';
            else if (domain.includes('youtube.')) domain = 'YouTube';
            else if (domain.includes('reddit.')) domain = 'Reddit';
            else if (domain.includes('whatsapp.')) domain = 'WhatsApp';

            // Ignore self-referrals
            if (!domain.includes('labofgames')) {
                return { trafficSource: domain, utmSource: domain.toLowerCase(), utmMedium: 'referral', utmCampaign };
            }
        } catch (e) {}
    }

    // 4. Default — no referrer, no params
    return { trafficSource: 'Direct', utmSource: '', utmMedium: '', utmCampaign };
};

const AnalyticsTracker = () => {
    const location = useLocation();
    const socketRef = useRef(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        if (!apiUrl) return; // No API configured — skip silently

        const trackView = async () => {
            try {
                let analyticsUuid = localStorage.getItem('analytics_uuid') || '';
                const { trafficSource, utmSource, utmMedium, utmCampaign } = resolveTrafficSource();

                const payload = {
                    path: location.pathname + location.search,
                    referrer: document.referrer || '',
                    trafficSource,
                    utmSource,
                    utmMedium,
                    utmCampaign,
                    analyticsUuid
                };

                const res = await fetch(`${apiUrl}/api/analytics/v2/event`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                const data = await res.json();
                
                if (data.success && data.analyticsUuid) {
                    localStorage.setItem('analytics_uuid', data.analyticsUuid);
                    
                    // Initialize WebSocket for real-time heartbeat tracking
                    if (!socketRef.current) {
                        socketRef.current = io(apiUrl, {
                            path: '/socket/live-traffic',
                            transports: ['websocket', 'polling'],
                            reconnectionAttempts: 5,
                            reconnectionDelay: 3000,
                        });
                        
                        socketRef.current.on('connect', () => {
                            socketRef.current.emit('heartbeat', {
                                analyticsUuid: data.analyticsUuid,
                                path: location.pathname,
                                timestamp: Date.now()
                            });
                        });
                    } else if (socketRef.current.connected) {
                        socketRef.current.emit('heartbeat', {
                            analyticsUuid: data.analyticsUuid,
                            path: location.pathname,
                            timestamp: Date.now()
                        });
                    }

                    // Setup periodic ping every 15s
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    intervalRef.current = setInterval(() => {
                        if (socketRef.current?.connected) {
                            socketRef.current.emit('heartbeat', {
                                analyticsUuid: data.analyticsUuid,
                                path: location.pathname,
                                timestamp: Date.now()
                            });
                        }
                    }, 15000);
                }
            } catch (error) {
                // Fail silently — never block the user experience
            }
        };

        trackView();
        
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [location]);

    return null;
};

export default AnalyticsTracker;
